/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useState, useCallback, useMemo } from 'react';
import { useNetworkStatus } from './useNetworkStatus';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Platform } from 'react-native';
import {
  useAccessToken,
  useApiKey,
  useIsAllStar,
  useIsLoggedIn,
  useUserId,
} from '@airasia-rn/account/hooks';
import { QUERY_PERSIST_CACHE, useAppConfig } from '@airasia-rn/ui';
import { useNearestAirportsCodes } from '@airasia-rn/flights/hooks';
import { RecommendationApiService } from '@airasia-rn/social';
import type {
  RecommendationApiResponse,
  RecommendationApiItem,
} from '../models/recommendTabResponse';
import {
  getRecommendationErrorInfo,
  RECOMMENDATION_NETWORK_ERROR_INFO,
  type RecommendationErrorInfo,
} from './recommendationErrorUtils';
import { useHomepageBlocks } from './useHomepage';

const RECOMMENDATION_STALE_TIME = 30 * 60 * 1000;

interface UsePromotionsParams {
  pageSize?: number;
  enabled?: boolean;
}

const promotionsKeys = {
  feed: (params: {
    country: string;
    city: string;
    nearestAirport: string;
    pageSize: number;
    currency: string;
  }) => [QUERY_PERSIST_CACHE, 'recommendation', 'promotions', params] as const,
};

export const usePromotions = ({
  pageSize = 20,
  enabled: isEnabled = true,
}: UsePromotionsParams = {}) => {
  const appConfig = useAppConfig();
  const api = useMemo(
    () => new RecommendationApiService(appConfig.config.env ?? 'stg'),
    [appConfig.config.env],
  );
  const accessTokenQuery = useAccessToken('homePromotionsRecommendations');
  const { isLoggedIn } = useIsLoggedIn('homePromotionsRecommendations');
  const userIdQuery = useUserId('homePromotionsRecommendations');
  const isAllStar = useIsAllStar('homePromotionsRecommendations');
  const apiKeyQuery = useApiKey();
  const nearestAirportQuery = useNearestAirportsCodes();
  const [refreshing, setRefreshing] = useState(false);
  const [refreshErrorInfo, setRefreshErrorInfo] =
    useState<RecommendationErrorInfo>();
  const [loadMoreError, setLoadMoreError] = useState(false);
  const [loadMoreErrorInfo, setLoadMoreErrorInfo] =
    useState<RecommendationErrorInfo>();

  const { responseCurrency } = useHomepageBlocks();
  const userId = userIdQuery.data ?? '';
  const clientId = apiKeyQuery.data?.id ?? '';
  const apiKey = apiKeyQuery.data?.apiKey ?? '';
  const locale = appConfig.config.locale ?? 'en-gb';
  const currency = (
    appConfig.config.currency ||
    responseCurrency ||
    'USD'
  ).toUpperCase();
  const nearestAirportLatLong = nearestAirportQuery.data?.latlong ?? '';
  const location = useMemo(() => {
    const [lat, lon] = nearestAirportLatLong
      .split(',')
      .map((value) => Number(value.trim()));

    return {
      country:
        appConfig.config.geoId || nearestAirportQuery.data?.country || 'MY',
      city: nearestAirportQuery.data?.city ?? '',
      nearestAirport: nearestAirportQuery.data?.airport?.[0] ?? '',
      latitude: Number.isFinite(lat) ? lat : 0,
      longitude: Number.isFinite(lon) ? lon : 0,
    };
  }, [
    appConfig.config.geoId,
    nearestAirportQuery.data?.airport,
    nearestAirportQuery.data?.city,
    nearestAirportQuery.data?.country,
    nearestAirportLatLong,
  ]);

  const { isConnected, isNetworkStatusKnown } = useNetworkStatus();
  const canQueryRecommendations =
    isEnabled && Boolean(clientId) && Boolean(apiKey);
  const canUseNetwork =
    Platform.OS === 'web' || isNetworkStatusKnown ? isConnected : true;
  const isLocationSettled = !nearestAirportQuery.isLoading;
  const enabled =
    canQueryRecommendations &&
    Boolean(appConfig.config.currency || responseCurrency) &&
    isLocationSettled &&
    canUseNetwork;
  const queryKey = useMemo(
    () =>
      promotionsKeys.feed({
        country: location.country,
        city: location.city,
        nearestAirport: location.nearestAirport,
        pageSize,
        currency,
      }),
    [
      location.country,
      location.city,
      location.nearestAirport,
      pageSize,
      currency,
    ],
  );

  const query = useInfiniteQuery<RecommendationApiResponse, Error>({
    queryKey,
    enabled,
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const pageNumber = typeof pageParam === 'number' ? pageParam : 1;
      const clientKey = await apiKeyQuery.getToken();
      const resolvedClientId = clientKey?.id ?? '';
      const resolvedApiKey = clientKey?.apiKey ?? '';
      if (!resolvedClientId || !resolvedApiKey) {
        throw new Error('Missing API key');
      }

      // Guests keep the previous request, which may have no access token.
      // Signed-in requests must use a freshly resolved token.
      let resolvedAccessToken = accessTokenQuery.data ?? '';
      if (isLoggedIn) {
        resolvedAccessToken = (await accessTokenQuery.getToken()) ?? '';
        if (!resolvedAccessToken) {
          throw new Error('Missing access token');
        }
      }

      const response = await api.fetchPromotions({
        accessToken: resolvedAccessToken,
        clientId: resolvedClientId,
        apiKey: resolvedApiKey,
        isAllStar,
        payload: {
          userId,
          page: pageNumber,
          pageSize,
          currency,
          locale,
          location,
          debug: false,
        },
      });

      return response;
    },
    getNextPageParam: (lastPage) =>
      lastPage.page * pageSize < lastPage.totalRecords
        ? lastPage.page + 1
        : undefined,
    staleTime: RECOMMENDATION_STALE_TIME,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    ...(Platform.OS === 'web'
      ? {}
      : {
          // Let TanStack revalidate when native connectivity recovers; the
          // enabled flag controls known-offline launches without an effect.
          refetchOnReconnect: 'always' as const,
        }),
    retry: 0,
    networkMode: 'always',
  });

  const items = useMemo<RecommendationApiItem[]>(() => {
    const seen = new Set<string>();
    return (query.data?.pages.flatMap((page) => page.data ?? []) ?? []).filter(
      (item) => {
        if (!item.lobItemId || seen.has(item.lobItemId)) return false;
        seen.add(item.lobItemId);
        return true;
      },
    );
  }, [query.data?.pages]);

  const prereqFailed =
    !apiKeyQuery.isLoading &&
    !accessTokenQuery.isLoading &&
    apiKeyQuery.isError;

  const {
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    data: queryData,
  } = query;

  const clearLoadMoreError = useCallback(() => {
    setLoadMoreError(false);
    setLoadMoreErrorInfo(undefined);
  }, []);

  const setLoadMoreErrorState = useCallback(
    (errorInfo: RecommendationErrorInfo) => {
      setLoadMoreError(true);
      setLoadMoreErrorInfo(errorInfo);
    },
    [],
  );

  const refresh = useCallback(async () => {
    if (!canQueryRecommendations) return;

    try {
      setRefreshing(true);
      setRefreshErrorInfo(undefined);
      clearLoadMoreError();
      const result = await refetch();
      setRefreshErrorInfo(
        result.isError ? getRecommendationErrorInfo(result.error) : undefined,
      );
    } finally {
      setRefreshing(false);
    }
  }, [canQueryRecommendations, clearLoadMoreError, refetch]);

  const loadMore = useCallback(async () => {
    if (!canQueryRecommendations || !hasNextPage || isFetchingNextPage) return;

    if (Platform.OS !== 'web' && isNetworkStatusKnown && !isConnected) {
      setLoadMoreErrorState(RECOMMENDATION_NETWORK_ERROR_INFO);
      return;
    }

    clearLoadMoreError();
    try {
      const previousPageCount = queryData?.pages.length ?? 0;
      const result = await fetchNextPage();
      const nextPageCount = result.data?.pages.length ?? previousPageCount;

      if (nextPageCount <= previousPageCount) {
        setLoadMoreErrorState({ code: undefined, message: 'Unknown error' });
      }
    } catch (error) {
      setLoadMoreErrorState(getRecommendationErrorInfo(error));
    }
  }, [
    canQueryRecommendations,
    clearLoadMoreError,
    hasNextPage,
    isConnected,
    isFetchingNextPage,
    isNetworkStatusKnown,
    fetchNextPage,
    queryData?.pages.length,
    setLoadMoreErrorState,
  ]);

  const retry = useCallback(() => {
    if (!canQueryRecommendations) return;
    void refetch();
  }, [canQueryRecommendations, refetch]);

  const loading =
    (accessTokenQuery.isLoading ||
      userIdQuery.isLoading ||
      apiKeyQuery.isLoading ||
      nearestAirportQuery.isLoading) &&
    items.length === 0;

  return {
    items,
    loading:
      loading || query.isLoading || (query.isFetching && items.length === 0),
    loadingMore: isFetchingNextPage,
    refreshErrorInfo,
    loadMoreError,
    loadMoreErrorInfo,
    error:
      (query.isError && !query.isFetching) ||
      (prereqFailed && items.length === 0),
    errorInfo: query.error
      ? getRecommendationErrorInfo(query.error)
      : undefined,
    hasMore: hasNextPage ?? false,
    loadMore,
    retry,
    refreshing,
    refresh,
  };
};
